import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Loader2, Clock, TrendingUp, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useProducts } from "@/hooks/useProducts";
import { ShopifyProduct } from "@/lib/shopify";

interface SearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RECENT_SEARCHES_KEY = "vapebolag_recent_searches";
const MAX_RECENT_SEARCHES = 5;

const popularSearches = [
  "Magic Sauce Vape",
  "THCA Hash",
  "Snozy",
  "Buds",
  "Gorilla Cookies",
];

export const SearchModal = ({ open, onOpenChange }: SearchModalProps) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Load recent searches from localStorage
  useEffect(() => {
    if (open) {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        try {
          setRecentSearches(JSON.parse(stored));
        } catch {
          setRecentSearches([]);
        }
      }
      // Focus input when modal opens
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Fetch products based on search query
  const { data: products, isLoading } = useProducts(
    10,
    debouncedQuery.length >= 2 ? `title:*${debouncedQuery}*` : undefined,
    false
  );

  const saveRecentSearch = useCallback((searchTerm: string) => {
    const trimmed = searchTerm.trim();
    if (!trimmed) return;
    
    setRecentSearches((prev) => {
      const filtered = prev.filter((s) => s.toLowerCase() !== trimmed.toLowerCase());
      const updated = [trimmed, ...filtered].slice(0, MAX_RECENT_SEARCHES);
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const handleSearch = (searchTerm: string) => {
    saveRecentSearch(searchTerm);
    navigate(`/sok?q=${encodeURIComponent(searchTerm)}`);
    onOpenChange(false);
    setQuery("");
  };

  const handleProductClick = (product: ShopifyProduct) => {
    saveRecentSearch(product.node.title);
    navigate(`/product/${product.node.handle}`);
    onOpenChange(false);
    setQuery("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && query.trim()) {
      handleSearch(query);
    }
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden bg-card border-border">
        <DialogTitle className="sr-only">Sök produkter</DialogTitle>
        
        {/* Search input */}
        <div className="flex items-center gap-3 p-4 border-b border-border">
          <Search className="w-5 h-5 text-muted-foreground flex-shrink-0" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Sök efter produkter..."
            className="border-0 bg-transparent focus-visible:ring-0 text-lg placeholder:text-muted-foreground"
          />
          {query && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 flex-shrink-0"
              onClick={() => setQuery("")}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Results area */}
        <div className="max-h-[60vh] overflow-y-auto">
          {/* Loading state */}
          {isLoading && debouncedQuery.length >= 2 && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          )}

          {/* Search results */}
          {!isLoading && products && products.length > 0 && debouncedQuery.length >= 2 && (
            <div className="p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                Produkter
              </p>
              <div className="space-y-1">
                {products.map((product) => (
                  <button
                    key={product.node.id}
                    onClick={() => handleProductClick(product)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-secondary transition-colors text-left"
                  >
                    <div className="w-12 h-12 rounded-lg bg-muted/30 overflow-hidden flex-shrink-0">
                      {product.node.images?.edges?.[0]?.node && (
                        <img
                          src={product.node.images.edges[0].node.url}
                          alt={product.node.title}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">
                        {product.node.title}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {parseFloat(product.node.priceRange.minVariantPrice.amount).toFixed(0)} kr
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </button>
                ))}
              </div>
              
              {/* View all results */}
              <Button
                variant="outline"
                className="w-full mt-3"
                onClick={() => handleSearch(query)}
              >
                Se alla resultat för "{query}"
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* No results */}
          {!isLoading && products?.length === 0 && debouncedQuery.length >= 2 && (
            <div className="p-8 text-center">
              <p className="text-muted-foreground mb-2">
                Inga produkter hittades för "{debouncedQuery}"
              </p>
              <Button variant="link" onClick={() => navigate("/kollektioner/alla-produkter")}>
                Se alla produkter →
              </Button>
            </div>
          )}

          {/* Default state - show recent & popular */}
          {(!debouncedQuery || debouncedQuery.length < 2) && (
            <div className="p-4 space-y-6">
              {/* Recent searches */}
              {recentSearches.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5" />
                      Senaste sökningar
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
                      onClick={clearRecentSearches}
                    >
                      Rensa
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {recentSearches.map((term) => (
                      <Button
                        key={term}
                        variant="secondary"
                        size="sm"
                        className="rounded-full"
                        onClick={() => handleSearch(term)}
                      >
                        {term}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Popular searches */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                  <TrendingUp className="w-3.5 h-3.5" />
                  Populära sökningar
                </p>
                <div className="flex flex-wrap gap-2">
                  {popularSearches.map((term) => (
                    <Button
                      key={term}
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={() => handleSearch(term)}
                    >
                      {term}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="p-3 border-t border-border bg-secondary/30 text-center">
          <p className="text-xs text-muted-foreground">
            Tryck <kbd className="px-1.5 py-0.5 bg-muted rounded text-foreground font-mono">Enter</kbd> för att söka
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
